import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Eye, CreditCard } from 'lucide-react'
import { PassPrintView } from '@/components/Accreditation/PassPrintView'
import type { PressPass } from '@/components/Accreditation/types'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalEmptyState } from './ui/PortalEmptyState'
import { PortalSkeletonGrid } from './ui/PortalSkeleton'

const STATUS_COLORS: Record<string, string> = {
  active:  'bg-success/15 text-success',
  expired: 'bg-muted text-muted-foreground',
  revoked: 'bg-destructive/15 text-destructive',
}

export function PortalPressPassView() {
  const [viewing, setViewing] = useState<PressPass | null>(null)

  const { data: passes, isLoading } = useQuery({
    queryKey: ['portal-press-passes'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user?.email) return []

      // Press passes are issued to journalists in the admin CRM, match by email
      const { data: journalist } = await (supabase
        .from('journalists' as any)
        .select('id, org_id, first_name, last_name, nationality, photo_url, verification_id, position')
        .eq('email', user.email)
        .maybeSingle() as any)

      if (!journalist) return []

      const { data: org } = await (supabase
        .from('organizations' as any)
        .select('name, logo_url, brand_primary, brand_accent')
        .eq('id', journalist.org_id)
        .maybeSingle() as any)

      const { data: passRows } = await (supabase
        .from('press_passes' as any)
        .select('id, pass_number, pass_type, accreditation_term, status, valid_from, valid_to, qr_payload, outlet_name, created_at')
        .eq('journalist_id', journalist.id)
        .order('created_at', { ascending: false }) as any)

      return ((passRows ?? []) as any[]).map(p => ({ ...p, journalist, organization: org ?? null })) as PressPass[]
    },
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PortalPageHeader title="My Press Passes" description="Passes issued to you by accredited organisations." />
        <PortalSkeletonGrid cards={3} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PortalPageHeader title="My Press Passes" description="Passes issued to you by accredited organisations." />

      {(passes ?? []).length === 0 ? (
        <PortalEmptyState
          icon={CreditCard}
          message="No press passes issued yet. Once an organisation approves your accreditation and issues a pass, it will appear here."
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(passes ?? []).map((pass: PressPass) => {
            const isExpired = new Date(pass.valid_to) < new Date()
            const effectiveStatus = pass.status === 'active' && isExpired ? 'expired' : pass.status
            return (
              <Card key={pass.id} className="overflow-hidden">
                <div className="h-1.5 bg-primary" />
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-justify font-mono font-bold text-sm text-success">{pass.pass_number}</p>
                      <p className="text-justify text-xs text-muted-foreground capitalize mt-0.5">{pass.pass_type} pass</p>
                    </div>
                    <Badge className={`text-[10px] ${STATUS_COLORS[effectiveStatus] ?? STATUS_COLORS.expired}`}>
                      {effectiveStatus}
                    </Badge>
                  </div>

                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <p>
                      Valid from:{' '}
                      <span className="text-foreground">
                        {new Date(pass.valid_from).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </p>
                    <p>
                      Valid to:{' '}
                      <span className={isExpired ? 'text-destructive font-medium' : 'text-foreground'}>
                        {new Date(pass.valid_to).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </span>
                    </p>
                  </div>

                  {pass.outlet_name && (
                    <p className="text-xs text-muted-foreground truncate">{pass.outlet_name}</p>
                  )}

                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5"
                    onClick={() => setViewing(pass)}
                  >
                    <Eye className="w-3.5 h-3.5" />
                    View Pass
                  </Button>
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      {viewing && <PassPrintView pass={viewing} canPrint={false} onClose={() => setViewing(null)} />}
    </div>
  )
}
